import { useState } from 'react';
import { Alert, Button, Flex, Skeleton, Tabs, Typography } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../../app/api-context';
import type { ImageObjectRow, OpsOverview, SearchImageRow } from '../../lib/ops-types';
import { OverviewTab } from './OverviewTab';
import { SearchImagesTab } from './SearchImagesTab';
import { ImagesTab } from './ImagesTab';
import { ProgressTab } from './ProgressTab';
import type { ProgressReport } from '../../lib/progress-types';

function Failed({ error, retry }: { error: unknown; retry: () => void }) {
  return <Alert type="error" showIcon message="조회에 실패했습니다" description={(error as Error)?.message} action={<Button size="small" onClick={retry}>다시 시도</Button>} />;
}

export function OpsDashboard() {
  const api = useApi();
  const qc = useQueryClient();
  const [refreshing, setRefreshing] = useState(false);
  const progress = useQuery({ queryKey: ['ops', 'progress'], queryFn: () => api.get<ProgressReport>('/progress') });
  const overview = useQuery({ queryKey: ['ops', 'overview'], queryFn: () => api.get<OpsOverview>('/ops/overview') });
  const search = useQuery({ queryKey: ['ops', 'search-images'], queryFn: () => api.get<{ items: SearchImageRow[] }>('/ops/search-images') });
  const images = useQuery({ queryKey: ['ops', 'images'], queryFn: () => api.get<{ items: ImageObjectRow[] }>('/ops/images'), retry: false });

  const refresh = async () => {
    // 서버 캐시(60초)를 건너뛰고 다시 조회
    setRefreshing(true);
    const [p, o, s, i] = await Promise.allSettled([
      api.get<ProgressReport>('/progress?fresh=1'),
      api.get<OpsOverview>('/ops/overview?fresh=1'),
      api.get<{ items: SearchImageRow[] }>('/ops/search-images?fresh=1'),
      api.get<{ items: ImageObjectRow[] }>('/ops/images?fresh=1'),
    ]);
    if (p.status === 'fulfilled') qc.setQueryData(['ops', 'progress'], p.value);
    if (o.status === 'fulfilled') qc.setQueryData(['ops', 'overview'], o.value);
    if (s.status === 'fulfilled') qc.setQueryData(['ops', 'search-images'], s.value);
    if (i.status === 'fulfilled') qc.setQueryData(['ops', 'images'], i.value);
    setRefreshing(false);
  };

  return (
    <>
      <Flex justify="space-between" align="center" wrap gap={8}>
        <div>
          <Typography.Title level={3} style={{ marginBottom: 4 }}>운영 대시보드</Typography.Title>
          <Typography.Text type="secondary">
            어휘·이미지 구축 현황과 CDN(R2)·Firebase 운영 데이터 조회 (읽기 전용){progress.data ? ` · ${new Date(progress.data.generatedAt).toLocaleString('ko-KR')} 기준` : ''}
          </Typography.Text>
        </div>
        <Button icon={<ReloadOutlined />} onClick={refresh} loading={refreshing || progress.isFetching || overview.isFetching}>새로 조회</Button>
      </Flex>
      <Tabs
        style={{ marginTop: 16 }}
        items={[
          {
            key: 'progress',
            label: '구축 현황',
            children: progress.isPending ? <Skeleton active /> : progress.isError ? <Failed error={progress.error} retry={() => progress.refetch()} /> : <ProgressTab data={progress.data} />,
          },
          {
            key: 'overview',
            label: '개요',
            children: overview.isPending ? <Skeleton active /> : overview.isError ? <Failed error={overview.error} retry={() => overview.refetch()} /> : <OverviewTab data={overview.data} />,
          },
          {
            key: 'search',
            label: `검색 이미지${search.data ? ` (${search.data.items.length})` : ''}`,
            children: search.isPending ? <Skeleton active /> : search.isError ? <Failed error={search.error} retry={() => search.refetch()} /> : <SearchImagesTab rows={search.data.items} />,
          },
          {
            key: 'images',
            label: `R2 이미지${images.data ? ` (${images.data.items.length})` : ''}`,
            children: images.isPending ? <Skeleton active /> : images.isError ? <Failed error={images.error} retry={() => images.refetch()} /> : <ImagesTab rows={images.data.items} />,
          },
        ]}
      />
    </>
  );
}
